/**
 * DateStatusBox - Reusable date display container for drawer headers
 *
 * Displays First Seen and Last Seen timestamps with consistent layout
 * and fixed minimum widths to prevent layout shifting.
 *
 * Used in: AssetDrawerHeader, VulnerabilityDrawerHeader, SeedDrawerHeader
 */

import { ClockIcon } from '@heroicons/react/24/outline';

import { formatDate } from '@/utils/date.util';

export interface DateStatusBoxProps {
  /** First Seen timestamp (ISO 8601 UTC) */
  firstSeen: string;
  /** Last Seen timestamp (ISO 8601 UTC) */
  lastSeen: string;
  /** Optional agent last scan timestamp (ISO 8601 UTC) — only rendered when present */
  agentLastScan?: string;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * DateStatusBox Component
 *
 * Renders a fixed-layout container displaying First Seen and Last Seen dates.
 * Uses minimum widths to prevent layout shifting as date values change.
 *
 * Layout:
 * - Container: 88px height, 200px minimum width
 * - Date labels: shrink-0 to prevent compression
 * - Date values: 110px minimum width to accommodate relative dates
 */
export function DateStatusBox({
  firstSeen,
  lastSeen,
  agentLastScan,
  className,
}: DateStatusBoxProps) {
  return (
    <div
      className={`bg-chariot-background-secondary flex min-h-[88px] min-w-[200px] shrink-0 flex-col justify-center gap-1 rounded-md px-3 py-4 ${className || ''}`}
    >
      <div className="flex items-center gap-1 text-sm">
        <ClockIcon className="text-chariot-text-secondary size-4" />
        <p className="shrink-0">First Seen:</p>
        <p className="min-w-[110px]">{formatDate(firstSeen)}</p>
      </div>
      <div className="flex items-center gap-1 text-sm">
        <ClockIcon className="text-chariot-text-secondary size-4" />
        <p className="shrink-0">Last Seen:</p>
        <p className="min-w-[110px]">{formatDate(lastSeen)}</p>
      </div>
      {agentLastScan && (
        <div className="flex items-center gap-1 text-sm">
          <ClockIcon className="text-chariot-text-secondary size-4" />
          <p className="shrink-0">Agent Scan:</p>
          <p className="min-w-[110px]">{formatDate(agentLastScan)}</p>
        </div>
      )}
    </div>
  );
}
